'use client'

import { usePathname } from 'next/navigation'
import { Fragment, useEffect, useRef } from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { useInView } from 'react-intersection-observer'
import { Post } from '@/payload-types'

import { useLatestPosts } from '@/hooks/useLatestPosts'
import { AYAdSlot } from '@/components/frontend/AYAdSlot'
import { ExpandablePost } from '@/components/frontend/ExpandablePost'
import Loader from '@/components/frontend/Loader'
import { SidebarAds } from '@/components/frontend/SidebarAds'
import { useLocale } from '@/providers/Locale'
import { cn } from '@/utilities/ui'
import { slugToLink } from '@/utilities/slugToLink'

export type Props = {
    firstPost?: Post
    initialPosts?: Post[]
    initialNextPage?: number
    changeUrlOnScroll?: boolean
    showSiderail?: boolean
}

export function InfiniteScrollPosts({ firstPost, initialPosts, changeUrlOnScroll, initialNextPage, showSiderail }: Props) {
    const pathname = usePathname()
    const originalPathname = useRef(pathname) // store for later so we can use if we scroll back up the page
    const { locale } = useLocale()

    const starterPosts = firstPost ? [firstPost, ...(initialPosts || [])] : initialPosts
    const { ref, inView } = useInView()

    const {
        loading,
        posts: loadedPosts,
        hasNextPage,
        fetchMorePosts,
    } = useLatestPosts(starterPosts, {
        initialNextPage,
        locale,
    })

    const [infiniteScrollRef] = useInfiniteScroll({
        loading,
        hasNextPage,
        onLoadMore: fetchMorePosts,
    })

    useEffect(() => {
        if (changeUrlOnScroll) {
            if (inView) {
                if (!window || loadedPosts.length < 1) {
                    return
                }

                const firstPost = loadedPosts[0]?.[0]

                if (!firstPost) {
                    return
                }

                const newUrl = `/gateway${slugToLink(firstPost.slug)}`
                window.history.replaceState(null, '', newUrl)
            } else {
                if (!window || pathname === originalPathname.current) {
                    return
                }

                window.history.replaceState(null, '', originalPathname.current)
            }
        }
    }, [pathname, loadedPosts, inView, changeUrlOnScroll])

    // use 66% if no sidebar
    return (
        <div className={cn('grid gap-8 grid-cols-1 py-[30px]', { 'md:grid-cols-[1.5fr_1fr]': showSiderail })} ref={ref}>
            {loadedPosts.map((posts, i) => (
                <Fragment key={i}>
                    <section className='max-w-[800px] px-[1.125rem]'>
                        {posts.map((post, j) => (
                            <Fragment key={post.id}>
                                <ExpandablePost post={post} />
                                {(j + 1) % 2 === 0 && (
                                    <div className='my-4'>
                                        <AYAdSlot adId='qz.com_336x280' width={336} height={280} />
                                    </div>
                                )}
                            </Fragment>
                        ))}
                    </section>
                    {showSiderail && (
                        <aside className='relative'>
                            <div className='sticky top-[0px]'>
                                <SidebarAds adCount={2} />
                            </div>
                        </aside>
                    )}
                </Fragment>
            ))}
            <div className='max-w-[800px] px-[1.125rem]'>
                {hasNextPage && (
                    <div ref={infiniteScrollRef} className='my-4'>
                        <Loader />
                    </div>
                )}
            </div>
        </div>
    )
}
